import { getAvailableActions as getClinicAvailableActions } from '@ldt/features/orders/components/order_actions/clinic';
import { hasLdtValidationErrors } from '@ldt/features/orders/utils/has_ldt_validation_errors';
import {
  CLINICAL_VARIABLES_MISSING_MESSAGE,
  isClinicalVariablesMissing,
  isOrderingProviderMissing,
  ORDERING_PROVIDER_MISSING_MESSAGE,
} from '@ldt/features/orders/utils/order_missing_info';
import type {
  CheckOrderValidationErrorsContext,
  GetAvailableActionsContext,
} from '@ldt/workflows/types';
import { LdtOrderAction } from '@ldt/workflows/types';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { isCustomerSuccessOrSalesUser, isLabTech } from 'src/utils';

const SLIDE_IMAGE_UPLOAD_STATUSES: ReadonlySet<OrderStatus> = new Set([
  OrderStatus.LabProcessing,
  OrderStatus.AiError,
  OrderStatus.SlideImageQualityControlFailed,
]);

/**
 * Direct Upload Lab actions:
 * - Lab Tech users can upload slide images on active upload statuses, or view/download
 *   report and slide image on Report Released orders. Other statuses have no actions.
 * - CS/Sales users get the same actions as the clinic workflow (by order status).
 * - Other roles are not supported (throw).
 */
export function getAvailableActions(
  ctx: GetAvailableActionsContext,
): LdtOrderAction[] {
  if (isLabTech()) {
    return getLabTechAvailableActions(ctx.order);
  }

  if (isCustomerSuccessOrSalesUser()) {
    return getClinicAvailableActions(ctx);
  }

  throw new Error(
    'Direct Upload Lab order actions are only available for lab tech or CS/Sales users.',
  );
}

export function getOrderMissingInfoMessages(
  order: LdtOrderDetailFieldsFragment,
): string[] {
  const messages: string[] = [];
  if (!isCustomerSuccessOrSalesUser()) return [];

  if (isClinicalVariablesMissing(order)) {
    messages.push(CLINICAL_VARIABLES_MISSING_MESSAGE);
  }

  if (isOrderingProviderMissing(order)) {
    messages.push(ORDERING_PROVIDER_MISSING_MESSAGE);
  }

  return messages;
}

export async function checkOrderValidationErrors(
  context: CheckOrderValidationErrorsContext,
): Promise<boolean> {
  return hasLdtValidationErrors(
    context.order,
    isCustomerSuccessOrSalesUser(),
    WorkflowType.DIRECT_UPLOAD_LAB,
    context.order.status === OrderStatus.QualityControlPending,
    context.order.specimenType,
  );
}

function getLabTechAvailableActions(
  order: LdtOrderDetailFieldsFragment,
): LdtOrderAction[] {
  const status = order.status;

  if (SLIDE_IMAGE_UPLOAD_STATUSES.has(status)) {
    return [LdtOrderAction.SlideImageSection];
  }

  if (
    status === OrderStatus.ReportReady ||
    status === OrderStatus.ReportFaxed
  ) {
    return [
      LdtOrderAction.DisplayPatientReport,
      LdtOrderAction.DownloadSlideImageQuery,
    ];
  }

  // Statuses not shown in the Direct Upload list (e.g. AI Processing): view-only.
  return [];
}
